/*
RISK-DOT-SCALE
Graphic element to display frequency / prevention and severity item
*/

import Tooltip, { TooltipProps } from "@/components/tooltip"
import { RISK_HELP_ITEMS, SimpleMetricKey } from "../model/risk.help.config"
import { Risk } from "../model/risk.types"
import { t } from "i18next"

type DotScaleProps = {
  risk: Risk
  id: SimpleMetricKey
  highlight?: boolean
  highlightColor?: string
  position?: TooltipProps["position"]
  onChange?: (val: number) => void
  tempValue?: number
}

export function RiskDotScale({
  risk,
  id,
  highlight = false,
  highlightColor = "#f59e0b",
  position = "top",
  onChange,
  tempValue
}: DotScaleProps) {
  const item = RISK_HELP_ITEMS.find((x) => x.valueKey === id)
  const colorFinal = id === "prevention" ? "#20CA7E" : "#9ca3af"

  const value = tempValue ?? (risk as any)?.[id] ?? 0

  function getMaxAndScale() {
    switch (id) {
      case "frequency":
        return { max: risk.frequencyScaleMax ?? getMaxFromScale(risk.frequencyScale), scale: risk.frequencyScale ?? [] }

      case "severity":
        return { max: risk.severityScaleMax ?? getMaxFromScale(risk.severityScale), scale: risk.severityScale ?? [] }

      case "prevention":
        return {
          max: risk.preventionScaleMax ?? getMaxFromScale(risk.preventionScale),
          scale: risk.preventionScale ?? []
        }
      default:
        return { max: 10, scale: [] }
    }
  }

  function getMaxFromScale(scale: any[] | undefined): number {
    if (!scale || scale.length === 0) return 10
    return Math.max(...scale.map((s) => s?.value ?? 0))
  }

  const { max, scale } = getMaxAndScale()

  const valueLabel = scale?.find((s: any) => s.value === value)?.label ?? ""
  const ratio = Math.min(value / (max || 1), 1)

  const perceptualRatio = Math.pow(ratio, 0.6)
  const opticalBoost = 1 - Math.pow(1 - ratio, 3) * 0.25
  const adjustedRatio = perceptualRatio * opticalBoost

  const minSize = 0.5
  const maxSize = 20
  const circleSize = minSize + (maxSize - minSize) * adjustedRatio

  // Arrondir la taille à un entier pair pour un rendu plus stable
  const roundedCircleSize = Math.round(circleSize / 2) * 2

  return (
    <Tooltip text={`${t(item?.name ?? id)} : ${valueLabel || value || "-"}`} position={position}>
      <div
        onClick={() => {
          if (!onChange || !max) return
          const next = value >= max ? 0 : value + 1
          onChange(next)
        }}
        className="relative flex items-center rounded-full transition-colors p-[1px] w-[40px]"
        style={{
          border: `1px solid ${highlight ? highlightColor : colorFinal}`
        }}>
        <div className="flex items-center justify-center" style={{ width: maxSize, height: maxSize }}>
          <div
            style={{
              width: roundedCircleSize,
              height: roundedCircleSize,
              borderRadius: "100%",
              backgroundColor: highlight ? highlightColor : colorFinal,
              transition: "all 0.25s ease"
            }}
          />
        </div>

        <span className="text-sm font-bold select-none flex-1 text-center mr-1">{value}</span>
      </div>
    </Tooltip>
  )
}
